/**
 * Data layer for the lifecycle activation "period summary" preview.
 *
 * Mirrors the job-selection logic in the DB trigger
 * `_stage_lifecycle_invoice_internal` (migration 20260504120300).
 * If the trigger's eligibility rules change, THIS must change too —
 * there is no auto-sync between the two.
 *
 * Selection rules (matching the trigger):
 *   1. window_start = MAX(changed_at)::date, in the TENANT's calendar, from
 *      customer_status_changes WHERE to_status = context AND
 *      changed_at <= (transitionDate + 1 day)'s tenant-local midnight, as a
 *      UTC instant. Falls back to customers.created_at::date (offset-less
 *      UTC — parsed via parseDbInstant), also projected into the tenant tz.
 *   2. Eligible jobs: job_context = context, date in [window_start, transitionDate],
 *      NOT already on a LIVE invoice (status IN draft/sending/sent/paid,
 *      deleted_at IS NULL — money-fences, 2026-09-17) snapshot.jobs[].id.
 *   3. Return jobs ordered by date asc, each with its line items.
 */

import { supabase } from '../supabase';
import { fetchAllRowsInChunks } from '../utils/async/fetchAllRowsInChunks';
import { getFieldReadTables } from '../utils/job/fieldReadTables';
import { getCurrentTenantRole } from '../utils/auth/currentTenantRoleStore';
import { parseDbInstant, toTenantDateStr, tenantMidnightUtcIso } from '../utils/date/tenantDate';

// A LIVE invoice — one that already claims a visit — for the billed-
// exclusion check. Widened from ('sent','paid') to also cover 'draft' and
// 'sending' (money-fences, 2026-09-17): a visit already staged on ANY
// not-yet-voided/deleted invoice must not double-stage into a second one.
// Exported: shared with lifecycleBundleServiceBulk.js's identical exclusion.
export const LIVE_INVOICE_STATUSES = ['draft', 'sending', 'sent', 'paid'];

/**
 * Can `role` SELECT from `invoices` at all? Mirrors
 * `invoices_select_admin_billing_same_tenant` — admin/billing ONLY,
 * regardless of price visibility: RLS never grants a field role (worker/
 * supervisor, even a price-VISIBLE one) or operations SELECT on invoices.
 * field-read-routing fix (2026-09-18, RD-ROLE-005b follow-up) — a blind
 * worker's period-bundle fetch used to query `invoices` unconditionally,
 * which has ALWAYS silently returned 0 rows for a field role (this is not a
 * new RD-ROLE-005b regression) and was silently read as "nothing is billed."
 *
 * A NULL/unresolved role fails OPEN to the pre-existing behavior (query
 * invoices) — same fail-open convention `fieldReadTablesForRole` uses: an
 * unresolved role never routes away from the table it would otherwise have
 * queried, so a legitimate admin/billing session mid-resolution never loses
 * its invoices read.
 *
 * B3 correction (RD-ROLE-005b, 2026-09-18): failing the QUERY open must not
 * also fail the CLAIM open. This predicate answers "should we run the
 * query", not "did we verify the result" — a null/unresolved role means the
 * caller doesn't yet know whether it's even ALLOWED to read invoices, so a
 * result read under that ambiguity cannot be presented as verified. Callers
 * must combine `!canReadInvoicesTable(role) || !role` when stamping
 * `billedExclusionUnknown` — see `fetchLifecyclePeriodBundle` /
 * `fetchBulkLifecyclePeriodBundles` below.
 */
export function canReadInvoicesTable(role) {
  return role !== 'worker' && role !== 'supervisor' && role !== 'operations';
}

/**
 * Fetch the lifecycle period bundle — the set of jobs that would be staged
 * into an opening or closing invoice for the given customer.
 *
 * @param {{
 *   tenantId: string,
 *   customerId: string,
 *   context: 'opening' | 'closing',
 *   transitionDate: string,  // 'YYYY-MM-DD' — the tenant-today when previewing
 *   tz?: string,  // IANA timezone; defaults to UTC
 * }} params
 * @returns {Promise<{
 *   jobs: Array<{
 *     id: string,
 *     date: string,
 *     total: number,
 *     unpriced_line_count: number,
 *     notes: string|null,
 *     ai_summary_customer: string|null,
 *     ai_summary_internal: string|null,
 *     ai_follow_ups: string|null,
 *     worker_id: string|null,
 *     worker_name: string|null,
 *     customer_id: string|null,
 *     job_context: string|null,
 *     lines: Array<{
 *       id: string|null,
 *       type: string|null,
 *       name: string|null,
 *       unit_price: number|null,
 *       quantity: number,
 *       catalog_id: string|null,
 *     }>,
 *   }>,
 *   windowStart: string,  // 'YYYY-MM-DD', in the tenant's calendar
 *   billedExclusionUnknown: boolean,  // GROUNDWORK ONLY (B4, 2026-09-18) —
 *     true when the caller could not VERIFY nothing is billed: either it has
 *     no `invoices` SELECT (a field role — the invoices query was skipped
 *     rather than silently treated as "nothing is billed", canReadInvoicesTable
 *     above) or its tenant role hasn't resolved yet. No consumer reads this
 *     field today. `jobs` surviving on a field-role bundle is therefore NOT a
 *     verified "confirmed unbilled" — it's "unbilled among what we could
 *     check" — and no future consumer may collapse that distinction.
 * }>}
 * @throws {Error} on any Supabase query failure
 */
export async function fetchLifecyclePeriodBundle({
  tenantId,
  customerId,
  context,
  transitionDate,
  tz = 'UTC',
}) {
  // ── 1. Resolve window_start ─────────────────────────────────────────────────
  // Mirrors: SELECT MAX(changed_at)::date FROM customer_status_changes
  //   WHERE tenant_id = … AND customer_id = … AND to_status = context
  //   AND changed_at <= p_transition_date::timestamptz + INTERVAL '1 day'
  //
  // PostgREST has no MAX() aggregate, so we ORDER BY desc + LIMIT 1 instead —
  // functionally identical because we only need the single most-recent row.
  // The "+ 1 day" upper bound is replicated with .lte('date', nextDay) below.
  const nextDay = addOneDay(transitionDate);

  const { data: changeRows, error: changeErr } = await supabase
    .from('customer_status_changes')
    .select('changed_at')
    .eq('tenant_id', tenantId)
    .eq('customer_id', customerId)
    .eq('to_status', context)
    // The bound is the UTC instant of TENANT-LOCAL midnight on nextDay — not
    // UTC midnight. An offset-less/UTC-anchored bound could pick a different
    // window_start than the server (which resolves "+1 day" in the tenant's
    // own calendar) near a day boundary in a non-UTC tenant tz.
    .lte('changed_at', tenantMidnightUtcIso(nextDay, tz))
    .order('changed_at', { ascending: false })
    .limit(1);

  if (changeErr) throw new Error(`lifecycleBundleService: window_start query failed — ${changeErr.message}`);

  let windowStart;

  if (changeRows && changeRows.length > 0 && changeRows[0].changed_at) {
    // changed_at is a timestamptz (always carries an offset) — project it
    // into the TENANT's calendar day, not the raw UTC date portion.
    windowStart = toTenantDateStr(changeRows[0].changed_at, tz);
  } else {
    // Fallback: customers.created_at::date (legacy customers pre-audit-table).
    // created_at is offset-less UTC (a `timestamp without time zone` column) —
    // parseDbInstant anchors it to UTC before re-projecting into tz.
    const { data: custRows, error: custErr } = await supabase
      .from(getFieldReadTables().customers)
      .select('created_at')
      .eq('id', customerId)
      .eq('tenant_id', tenantId)
      .limit(1);

    if (custErr) throw new Error(`lifecycleBundleService: customer fallback query failed — ${custErr.message}`);

    const raw = custRows?.[0]?.created_at;
    if (!raw) throw new Error(`lifecycleBundleService: customer ${customerId} not found`);
    windowStart = toTenantDateStr(parseDbInstant(raw), tz);
  }

  // ── 2. Fetch in-context jobs + billed-exclusion invoices in parallel ────────
  // These two queries are independent (both only depend on windowStart from
  // step 1), so running them concurrently shaves one round-trip.
  const isBaseJobsTable = getFieldReadTables().jobs === 'jobs';
  // A field role (worker/supervisor) — visible or blind — never has base
  // `invoices` SELECT (canReadInvoicesTable, above); querying it is a
  // wasted round-trip that always returns 0 rows. Skip it entirely rather
  // than silently treating that 0-row read as "nothing is billed."
  const canReadInvoices = canReadInvoicesTable(getCurrentTenantRole());
  const [jobsResult, invoicesResult] = await Promise.all([
    supabase
      .from(getFieldReadTables().jobs)
      // RD-ROLE-005b: a blind field role reads `field_jobs`, which has no
      // `total`/`unpriced_line_count` column — selecting either there is a
      // hard error, not 0 rows.
      .select(isBaseJobsTable
        ? 'id, date, total, unpriced_line_count, notes, ai_summary_customer, ai_summary_internal, ai_follow_ups, worker_id, worker_name, customer_id, job_context'
        : 'id, date, notes, ai_summary_customer, ai_summary_internal, ai_follow_ups, worker_id, worker_name, customer_id, job_context')
      .eq('tenant_id', tenantId)
      .eq('customer_id', customerId)
      .eq('job_context', context)
      .gte('date', windowStart)
      .lte('date', transitionDate)
      .order('date', { ascending: true }),
    // Billed exclusion — mirrors the trigger's NOT EXISTS subquery. Widened
    // (money-fences, 2026-09-17) from ('sent','paid') to every LIVE status —
    // a visit already on ANY not-yet-voided/deleted invoice (including a
    // draft or an in-flight send) must not double-stage into a second one.
    // We extract job ids from snapshot.jobs[].id (same shape as buildInvoiceSnapshot).
    canReadInvoices
      ? supabase
        .from('invoices')
        .select('snapshot')
        .eq('tenant_id', tenantId)
        .eq('customer_id', customerId)
        .in('status', LIVE_INVOICE_STATUSES)
        .is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const { data: jobRows, error: jobsErr } = jobsResult;
  const { data: invoiceRows, error: invErr } = invoicesResult;
  // B3 (2026-09-18): the query itself fails open on a null role
  // (canReadInvoicesTable(null) === true), but the CLAIM "we verified
  // nothing is billed" must not — a null role hasn't yet resolved enough to
  // know whether it's even entitled to that read.
  const billedExclusionUnknown = !canReadInvoices || !getCurrentTenantRole();

  if (jobsErr) throw new Error(`lifecycleBundleService: jobs query failed — ${jobsErr.message}`);

  if (!jobRows || jobRows.length === 0) {
    return { jobs: [], windowStart, billedExclusionUnknown };
  }

  // ── 3. Billed exclusion ──────────────────────────────────────────────────────
  if (invErr) throw new Error(`lifecycleBundleService: invoices query failed — ${invErr.message}`);

  const billedJobIds = buildBilledJobIdSet(invoiceRows ?? []);

  const survivingJobs = jobRows.filter((j) => !billedJobIds.has(String(j.id)));

  if (survivingJobs.length === 0) {
    return { jobs: [], windowStart, billedExclusionUnknown };
  }

  // ── 4. Fetch line items for surviving jobs ──────────────────────────────────
  const survivingIds = survivingJobs.map((j) => j.id);

  const { data: lineRows, error: lineErr } = await fetchAllRowsInChunks(survivingIds, (chunk, opts) => supabase
    .from('job_line_items')
    .select('id, job_id, type, name, unit_price, quantity, catalog_id', opts)
    .eq('tenant_id', tenantId)
    .in('job_id', chunk)
    .order('id'));

  if (lineErr) throw new Error(`lifecycleBundleService: line items query failed — ${lineErr.message}`);

  // Group line items by job_id
  const linesByJobId = new Map();
  for (const line of lineRows ?? []) {
    if (!line?.job_id) continue;
    const key = String(line.job_id);
    if (!linesByJobId.has(key)) linesByJobId.set(key, []);
    linesByJobId.get(key).push({
      id: line.id ?? null,
      type: line.type ?? null,
      name: line.name ?? null,
      // NEVER coerce a NULL unit_price to 0 — null means "office prices it"
      // (RD-ROLE-005); 0 means a real, deliberate free line. Money doctrine:
      // absence and zero are different answers (CLAUDE.md).
      unit_price: line.unit_price ?? null,
      quantity: line.quantity ?? 1,
      catalog_id: line.catalog_id ?? null,
    });
  }

  // ── 5. Assemble return shape ────────────────────────────────────────────────
  const jobs = survivingJobs.map((j) => ({
    id: String(j.id),
    date: j.date,
    total: j.total ?? 0,
    unpriced_line_count: isBaseJobsTable ? (j.unpriced_line_count ?? 0) : 0,
    notes: j.notes ?? null,
    ai_summary_customer: j.ai_summary_customer ?? null,
    ai_summary_internal: j.ai_summary_internal ?? null,
    ai_follow_ups: j.ai_follow_ups ?? null,
    worker_id: j.worker_id ?? null,
    worker_name: j.worker_name ?? null,
    customer_id: j.customer_id ?? null,
    job_context: j.job_context ?? null,
    lines: linesByJobId.get(String(j.id)) ?? [],
  }));

  return { jobs, windowStart, billedExclusionUnknown };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build a flat Set of billed job IDs from an array of invoice rows with
 * `snapshot` columns. Only reads `snapshot.jobs[].id` (the shape produced by
 * buildInvoiceSnapshot). Accepts both parsed-object and JSON-string snapshots.
 *
 * @param {Array<{ snapshot?: any }>} invoiceRows
 * @returns {Set<string>}
 */
export function buildBilledJobIdSet(invoiceRows) {
  const ids = new Set();
  for (const inv of invoiceRows) {
    let snap = inv?.snapshot;
    if (!snap) continue;
    if (typeof snap === 'string') {
      try { snap = JSON.parse(snap); } catch { continue; }
    }
    const jobs = Array.isArray(snap?.jobs) ? snap.jobs : [];
    for (const j of jobs) {
      if (j?.id != null) ids.add(String(j.id));
    }
  }
  return ids;
}

// The BULK sibling (fetchBulkLifecyclePeriodBundles / scanBulkLifecycleDuplicates)
// lives in lifecycleBundleServiceBulk.js — extracted 2026-09-17 to keep this
// file under the 500-line cap. Re-exported here so every existing caller's
// `import { fetchBulkLifecyclePeriodBundles } from '.../lifecycleBundleService'`
// keeps working unchanged.
export { fetchBulkLifecyclePeriodBundles, scanBulkLifecycleDuplicates } from './lifecycleBundleServiceBulk';

/**
 * Return the calendar day after `dateStr` ('YYYY-MM-DD').
 * Used to replicate the trigger's `<= transitionDate::timestamptz + INTERVAL '1 day'`
 * bound on changed_at.
 *
 * @param {string} dateStr  'YYYY-MM-DD'
 * @returns {string}        'YYYY-MM-DD'
 */
export function addOneDay(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
